import { CirclePacking } from '@innovation-os-ng/ui/chart-ng';
import { CompoundFilter, IqlQuery, LeafFilter } from '@itonics/filters';
import { Subject } from 'rxjs';
import { FactType } from '../../facts/interfaces/fact';
import { BarGroupSelectorType, ItemTitleCasing } from '../model/model-bar-chart';
import { MetricFunction, MetricOptions, MetricsAggregation, MetricsSwitch } from './metric';

export enum FilterChangeAction {
  ADD,
  REMOVE
}

/**
 * FILTERS
 */
export interface InsightsFilterOption {
  id: string;
  label: string;
}

/**
 * Definition of CompoundFilter IDs
 */
export enum SignalsFilters {
  SEARCH_FIELD = 'sf',
  SELECTION = 'sl',
  COMBINED = 'cb',
  SLECTION_SEARCHFIELD = 'slsf' // for searchfields inside selection query
}

/**
 * Definition of LeafFilter ONs/IDs
 */
export enum SignalsType {
  DOCUMENT_TYPE = 'type',
  SOURCE = 'source',
  ENTITY = 'entities',
  PUBLISHED_DATE = 'publishedDate',
  CATEGORY = 'iptcCategories.label.hierarchical',
  COUNTRY = 'country',
  COMPANY = 'company',
  PERSON = 'entities.person',
  TOPICS = 'topic',
  TOPICS_OLD = 'topics.label.raw',
  TOPICS_AGGREGATE = 'topics',
  INDUSTRY = 'industry',
  STEEP = 'steep',
  SEARCHFIELD = 'slsf',
  COMPANIES = 'companies',
  STARTUPS = 'startUps',
  TECHNOLOGIES = 'technologies',
  PRODUCTS = 'products',
  INDUSTRIES = 'industries',
  LANGUAGE = 'language',
  PEOPLE = 'person',
  PEOPLE_OLD = 'entities.people.label',
  PEOPLE_AGGREGATE = 'persons',
  PLACES = 'place',
  PLACES_OLD = 'entities.places.label',
  PLACES_AGGREGATE = 'places',
  ORGANIZATIONS = 'organization',
  ORGANIZATIONS_OLD = 'entities.organisations.label',
  ORGANIZATIONS_AGGREGATE = 'organizations',
  ENTITY_URIS = 'entityUris',
  EMPTY_AGGREGATE = 'entitiesList.uris'
}

/**
 * DATE
 */
export interface SignalsDatePicker {
  jsdate?: Date;
  endJsDate: Date;
  beginJsDate?: Date;
}

/**
 * DOCUMENTS
 */
export interface InsightsDocument {
  /**
   * Document id
   */
  uri: string;

  /**
   * Type of the document
   */
  type: string;

  /**
   * Title of the document
   */
  label: string;

  /**
   * Summary of the document
   */
  summary: string;

  /**
   * Description of the document, rich text field
   */
  description?: string;

  /**
   * URL of the document
   */
  url?: string;

  /**
   * Source of the document
   * Basically it's the base domain of the URL or USPTO/EPO for patents
   */
  source?: string;

  /**
   * Language of the document
   */
  language?: string;

  /**
   * Date when the document was published
   */
  publishedDate?: number;

  /**
   * country specified in document
   */
  country?: string;
  imageUrl?: string;

  /**
   * ************************************
   * RSS feed specific properties
   * ************************************
   */

  /**
   * Authors name specified in RSS/Atom feed
   */
  authors?: Array<string>;

  /**
   * Categories specified in RSS/Atom feed
   * can have multiple values
   */
  categories?: Array<string>;

  /**
   * ************************************
   * Patent specific properties
   * ************************************
   */

  /**
   * Patent brief description, rich text field
   */
  patentBriefDescription?: string;

  /**
   * Patent priority dates, can be multiple
   */
  patentPriorityDates?: Array<number>;

  /**
   * Patent applicants
   */
  patentApplicants?: Array<string>;

  /**
   * Patent inventors
   */
  patentInventors?: Array<string>;

  /**
   * Patent assignees
   */
  patentAssignees?: Array<string>;

  /**
   * Patent claims
   */
  patentClaims?: Array<string>;

  /**
   * Metadata of the document
   * Generally its key-value pairs,
   * specifying details about the source of the document and other details
   */
  _metadata: { [key: string]: any };

  createdOn?: number;
  updatedOn?: number;

  iptcCategories?: Array<{
    label: string;
    id: string;
  }>;
}

/**
 * Aggregated data
 */
export interface SignalsAggregateTimeSeries {
  date: string;
  value: number;
  label?: string;
  category: string;
  ranking?: number;
}

export interface SignalsAggregateWorldMap {
  value: number;
  country: string;
  category: string;
  label: string;
}

export interface SignalsAggregateData {
  id: string;
  label: string;
  hierarchicalLabel?: string;
  value: number;
  ranking?: number;
  highlightedValue?: number;
  colorCategory?: string;
  checked?: boolean;
}

/**
 * Aggregate Data with extra properties to be used in the hierarchical dropdown
 * Extends CirclePacking because that type is used for topics aggregate data
 */
export interface SignalsHierarchicalAggregateTopics extends SignalsAggregateData, CirclePacking {
  _depth: number;
  _ancestors: Array<SignalsHierarchicalAggregateTopics>;
  _descendants: Array<SignalsHierarchicalAggregateTopics>;
}

/**
 * Aggregate data the user has selected
 */
export interface SignalsSelectedModel {
  selected: Array<SignalsAggregateData>;
  /**
   * Flag indicating if the global filter should be updated
   * as a result of this selection action
   */
  updateGlobal: boolean;
}

export interface SignalsAggregateResult {
  total: number;
  aggregateData: Array<SignalsAggregateData>;
  aggregatesArePaginated: boolean; // Flag for FE use only (to indicate in the UI if there are more results)
}

export enum AggregateDataType {
  NOT_ASSIGNED = 'notAssigned',
  OTHER = 'other'
}

// Panels (filter or visualization)
export interface SignalsBasePanel {
  type: SignalsType | SignalsVisualization;
  label?: string;
  selected?: boolean;
  featureFlag?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  order?: number;
}

export enum SignalsPanelType {
  CHECKBOXES = 'checkboxes',
  CUSTOM = 'custom',
  PROPERTY = 'property',
  SPECIAL_PROPERTY = 'special'
}

// Filter Panels
export interface SignalsPanel extends SignalsBasePanel {
  type: SignalsType;
  oldType?: SignalsType;
  aggregateType?: SignalsType;
  emptyAggregateType?: SignalsType;
  selectionType?: BarGroupSelectorType;
  showAsFilter?: boolean;
  dictionary: SignalsDictionary;
  panelType?: SignalsPanelType;
  titleCase?: ItemTitleCasing;
  factEntity?: boolean;
  factType?: FactType;
  icon?: string;
}

// Visualization Panels
export interface SignalsVizPanel extends SignalsBasePanel {
  type: SignalsVisualization;
}

/**
 * REQUESTED DATA
 */

export interface TimeSeriesAggregateData {
  aggregations: Array<{
    doc_count: number;
    publishedDate: number;
    type: string;
    country?: string;
  }>;
}

export interface QueryResults<T> {
  items?: Array<T>;

  paginationCursor?: { [key: string]: string }; // for pagination

  total?: number;
}

/**
 * API
 */

//   QUERY
export interface AggregationParam {
  /**
   * Name of property to use for aggregation
   */
  property: string;
  metricsAggregations?: Array<string>;

  /**
   * The value that should be returned as the key in the aggregation results for above property mising document counts
   */
  returnMissingValuesAs?: string | number;

  /**
   * Size of the aggregations
   * It specifies the no. of top aggregations to return, default is 100
   */
  aggregationSize?: number;

  /**
   * Metric to use for aggregation
   * relevance: search result relevance
   * count: signals count [Default]
   */
  metric?: MetricsAggregation;

  /**
   * Which function to use while calculating the aggregation
   * Only applicable for non-`count` aggregations
   * `sum` is the Default one
   */

  metricFunction?: MetricFunction;
}
export interface DocumentsQueryPostBody {
  aggregations: Array<string | AggregationParam>;
  query: IqlQuery;
  limit: number;
  executionDetails?: QueryExecutionRequest;
  metric?: MetricsAggregation;
}

export interface DocumentsQueryAggregation {
  key: string | TimeRange;
  doc_count?: number;
  value?: number;
  childAggregations?: {
    [key: string]: Array<DocumentsQueryAggregation>;
  };
}
export interface DocumentsQueryResult {
  items: Array<InsightsDocument>;
  total: number;
  aggregation: {
    [key: string]: Array<DocumentsQueryAggregation>;
  };
  executionDetails?: QueryExecutionResponse;
}

export interface NormalizedDataResponse {
  items: Array<InsightsDocument>;
  total: number;
  aggregation: {
    [key: string]: Array<DocumentsQueryAggregation>;
  };
  executionDetails?: QueryExecutionResponse;
  maxNormalizerCount: number;
  foresightProcessCompleted?: boolean;
  showAggregate: boolean;
}

// Aggregation request that can be combined with other aggregation requests with the same query
export interface CombinableAggregationRequest {
  type: string;
  query: CompoundFilter;
  metricOptions: MetricOptions;
  ids: Array<string>;
  postBody: DocumentsQueryPostBody;
  responseSub: Subject<DocumentsQueryResult>;
  async?: boolean;
}

export interface EntityDetailQueryPostBody {
  uris: Array<string>;
  fields?: Array<string>;
}

export interface EntityDetailQueryPostResult {
  uri: string;
  properties: {
    'rdf:type': Array<string>;
    'rdfs:label': Array<string>;
  };
}

//   TIME SERIES

export interface TimeRange {
  from: number;
  to: number;
}

export interface DocumentsTimeSeriesQueryResult {
  items: Array<InsightsDocument>;
  total: number;
  aggregation: {
    [key: string]: Array<DocumentsQueryAggregation>;
  };
}

export interface DocumentsTimeSeriesPostBody {
  searchFieldUri?: string;
  timeRanges: Array<number>;
  timeAggregationProperty: string;
  aggregations: Array<string>;
  query: IqlQuery;
}

// TOPICS
export interface DocumentsTopicsPostBody {
  topicSource: string;
  query: IqlQuery;
  executionDetails?: QueryExecutionRequest;
  metric?: MetricsAggregation;
}

/**
 * Model for topic dictionary.
 */
export interface TopicDictionary {
  id: string;
  label: string;
  parent: string;
  hierarchicalLabel: string;
}

export interface DocumentsTopic extends TopicDictionary {
  doc_count: number;
  children: Array<DocumentsTopic>;
  childrenOnlyCount: number;
  value: number;
}
export interface DocumentsTopicsResult {
  topics: Array<DocumentsTopic>;
  executionDetails?: QueryExecutionResponse;
}

export interface DocumentsTimeSeriesAggregation {
  doc_count: number;
  publishedDate: number;
  type?: string;
  country?: string;
}
export interface DocumentsTimeSeriesResult {
  aggregations: Array<DocumentsTimeSeriesAggregation>;
}

/**
 * Labels
 */

export interface SignalsLabelMap {
  [key: string]: {
    label: string;
  };
}

/**
 * GROWTH INDICATOR
 */

export interface SignalsGrowthIndicator {
  country: string;
  growth: number;
}

/**
 * Visualizations
 */
export enum SignalsVisualization {
  TIMELINE = 'timeline',
  WORLDMAP = 'worldmap',
  CIRCLE_PACKING = 'circle-packing',
  // POC
  TOPIC_CLUSTERS = 'topic-clusters'
}

/**
 * GENERALS
 */
export interface SignalsDictionary {
  [key: string]: string;
}

/**
 * INFO SELECTION
 */

export interface SelectionInfoQuery {
  label: string;
  values: SelectionInfoQueryValue | Array<SelectionInfoQueryValue>;
  on: string;
  exclude: boolean;
}

export interface SelectionInfoQueryValue {
  label: string;
  id: string;
}

export interface CountRequest {
  countKey: string;
  query: IqlQuery;
}

/**
 * ui-date-picker Alignment
 */
export enum DatePickerAlignment {
  LEFT = 'left',
  RIGHT = 'right',
  BOTTOM = 'bottom',
  TOP = 'top'
}

/**
 * ui-menu-container Triggers
 */
export enum MenuContainerTriggers {
  CLICK = 'click',
  HOVER = 'hover',
  MOUSEDOWN = 'mousedown',
  NONE = 'none'
}

/**
 * ui-menu-container Placements
 */
export enum MenuContainerPlacements {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  TOP_START = 'top-start',
  BOTTOM_START = 'bottom-start',
  LEFT_START = 'left-start',
  RIGHT_START = 'right-start',
  TOP_END = 'top-end',
  BOTTOM_END = 'bottom-end',
  LEFT_END = 'left-end',
  RIGHT_END = 'right-end',
  AUTO = 'auto',
  AUTOSTART = 'auto-start',
  AUTO_END = 'auto-end'
}

/**
 * ui-menu-container className
 */
export enum MenuContainerClassName {
  BLANK_CONTAINER = 'blank-container'
}

/**
 * Typings for the executionDetail param of aggregate query requests / responses
 */
// In the request, it's used for status updates as well as in initial async or sync requests
export type QueryExecutionRequest = QueryExecutionRequestAsync | QueryExecutionRequestStatus | QueryExecutionRequestSync;
// In the response, it contains the status of the async query
export type QueryExecutionResponse = QueryExecutionResponseStatus | QueryExecutionResponseFailed;

// In async requests:
//   'full' will submit both the results list AND aggregations asynchronously
//   'aggregations' will return the results list synchronously but the aggregations asynchronously
export enum ExecutionDetailAsync {
  Full = 'full',
  Aggregations = 'aggregations'
}
export interface QueryExecutionRequestAsync {
  asynchronous: ExecutionDetailAsync;
}
// In sync requests, set 'useCache: true' to allow the backend to retrieve cached results (if they exist)
export interface QueryExecutionRequestSync {
  useCache: boolean;
}

// When requesting status, we just need to pass the asyncId
export interface QueryExecutionRequestStatus {
  asyncId: string;
}

// In the response, we'll get an asyncId and one of the following statuses
export enum ExecutionDetailStatus {
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed'
}
export interface QueryExecutionResponseStatus {
  asyncId: string;
  status: ExecutionDetailStatus;
}

// In the case of an error, we'll get a 'failed' status and an error message
export interface QueryExecutionResponseFailed extends QueryExecutionResponseStatus {
  status: ExecutionDetailStatus.Failed;
  error: string;
}

export interface DefaultMetricType {
  label: string;
  value: MetricsSwitch;
}

export enum ModelKey {
  Label = 'label',
  Id = 'id'
}

// Update to be emitted with new filter and appropriate action
export interface FilterUpdate {
  action: FilterChangeAction;
  filter: LeafFilter | CompoundFilter;
}
